// Manages populating the main data hub table with an aggregated view of all user data.

import { collection, getDocs } from 'firebase/firestore'
import { db } from '@/core/firebase'
import { currentUser } from '@/core/state'
import { paths } from '../db/paths'
import { getAccountContext } from '../store/api' // FIXED: Import getAccountContext directly
import { showLoading, hideLoading } from '@/core/ui' // FIXED: Added missing UI imports
import { initializeDataHubListeners } from './listeners' // FIXED: Added missing listener import

// FIXED: Define the interface used in the code
interface CompiledData {
  salesReports: any[]
  pnlReports: any[]
}

/**
 * Fetches all user data (sales, targets, P&L) associated with their branches
 * and populates the main data hub table.
 */
export async function populateCompiledDataTable() {
  if (!currentUser) return

  // FIXED: Correct way to access store context
  const { userBranches } = getAccountContext() 
  
  const tableContainer = document.getElementById('data-hub-table-container')

  if (!userBranches || userBranches.length === 0) {
    if (tableContainer) tableContainer.innerHTML = '<p class="text-gray-500 p-4 text-center">No branches found. Please contact your administrator.</p>'
    return
  }

  showLoading({ message: 'Fetching data reports...', value: 10 })

  try {
    // 1. Fetch data concurrently
    const queries = userBranches.flatMap((branch) => {
      // Only fetch reports, not targets (as per your simplified logic)
      return [
        getDocs(collection(db, paths.branches(branch.id).salesReports.collectionPath)),
        getDocs(collection(db, paths.branches(branch.id).pnlReports.collectionPath)),
      ]
    })

    // Filter out any undefined promises before awaiting
    const querySnapshots = await Promise.all(queries)
    hideLoading()

    // 2. Compile and organize data by period and branch name
    const compiledData: Record<string, Record<string, CompiledData>> = {} 

    let snapshotIndex = 0
    for (const branch of userBranches) {
      const branchName = branch.branchName || 'Unknown Branch'
      
      // Process Sales Reports
      const salesReportSnap = querySnapshots[snapshotIndex++]
      salesReportSnap.docs.forEach((doc) => {
        const data = doc.data() as any
        const period = data.period || 'No Period'
        
        if (!compiledData[period]) compiledData[period] = {}
        if (!compiledData[period][branchName]) compiledData[period][branchName] = { salesReports: [], pnlReports: [] }

        compiledData[period][branchName].salesReports.push(data)
      })

      // Process P&L Reports
      const pnlReportSnap = querySnapshots[snapshotIndex++]
      pnlReportSnap.docs.forEach((doc) => {
        const data = doc.data() as any
        const period = data.period || 'No Period'

        if (!compiledData[period]) compiledData[period] = {}
        if (!compiledData[period][branchName]) compiledData[period][branchName] = { salesReports: [], pnlReports: [] }

        compiledData[period][branchName].pnlReports.push(data)
      })
    }

    // 3. Render the table
    let tableHtml = `
      <div class="overflow-x-auto">
          <table class="min-w-full divide-y divide-gray-200">
              <thead class="bg-gray-50">
                  <tr>
                      <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Period</th>
                      <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Branch</th>
                      <th class="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">P&L Report</th>
                      <th class="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Sales Report</th>
                      <th class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
              </thead>
              <tbody class="bg-white divide-y divide-gray-200">
      `
    const sortedPeriods = Object.keys(compiledData).sort((a, b) => b.localeCompare(a))

    if (sortedPeriods.length === 0) {
      tableHtml += `<tr><td colspan="5" class="px-6 py-4 text-center text-sm text-gray-500">No data found. Upload a file to get started.</td></tr>`
    }

    sortedPeriods.forEach((period) => {
      const branchesInPeriod = Object.keys(compiledData[period]).sort()

      branchesInPeriod.forEach((branchName) => {
        const data = compiledData[period][branchName]
        tableHtml += renderRow(period, branchName, data, userBranches.find((b) => b.branchName === branchName)?.id)
      })
    })

    tableHtml += `</tbody></table></div>`

    if (tableContainer) tableContainer.innerHTML = tableHtml
    
    // Reattach listeners for upload buttons (if any)
    initializeDataHubListeners() 

  } catch (error) {
    console.error('Error populating table:', error)
    hideLoading()
    if (tableContainer) tableContainer.innerHTML = '<p class="text-red-500 p-4 text-center">Failed to load data.</p>'
  }
}

/**
 * Renders a single row in the data hub table.
 */
function renderRow(period: string, branchName: string, data: CompiledData, branchId?: string) {
  const pnlReportStatus = data.pnlReports.length > 0
    ? `<span class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">OK (${data.pnlReports.length})</span>`
    : `<span class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">Missing</span>`
  
  const salesReportStatus = data.salesReports.length > 0
    ? `<span class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">OK (${data.salesReports.length})</span>`
    : `<span class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">Missing</span>`

  // Buttons use data-attributes which are picked up by initializeDataHubListeners
  const uploadSalesButton = `<button data-branch-name="${branchName}" data-branch-id="${branchId}" class="upload-data-btn text-indigo-600 hover:text-indigo-900 ml-2 text-xs font-medium">Upload Sales</button>`
  const uploadPnlButton = `<button data-branch-name="${branchName}" data-branch-id="${branchId}" class="upload-pnl-btn text-indigo-600 hover:text-indigo-900 ml-2 text-xs font-medium">Upload P&L</button>`

  return `
    <tr>
      <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">${period}</td>
      <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">${branchName}</td>
      <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center">${pnlReportStatus}</td>
      <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center">${salesReportStatus}</td>
      <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-right">
        ${uploadSalesButton}
        ${uploadPnlButton}
      </td>
    </tr>
  `
}